<template>
  <available-section section="task-module" style="width: 100%">
    <main class="task-list">
      <div class="task-list__body">
        <div class="task-list__header">
          <h1 class="task-list__title">Задачи</h1>
          <button
            type="button"
            v-on:click="showTaskCreationForm"
            class="task-list__button-add"
          >
            Добавить задачу
          </button>
        </div>
        <div class="task-list__filters">
          <v-autocomplete
            class="task-list__autocomplete"
            label="Автор"
            hide-details
            clearable
            item-text="title"
            item-value="value"
            v-bind:items="filterPerformerList"
            v-model="authorId">
            <template v-slot:no-data>
              <p style="padding: 10px; margin:0;">Результатов нет</p>
            </template>
          </v-autocomplete>
          <v-autocomplete
            class="task-list__autocomplete task-list__autocomplete--executor"
            label="Исполнитель"
            hide-details
            clearable
            item-text="title"
            item-value="value"
            v-bind:items="filterPerformerList"
            v-model="performerId">
            <template v-slot:no-data>
              <p style="padding: 10px; margin:0;">Результатов нет</p>
            </template>
          </v-autocomplete>
          <v-autocomplete
            class="task-list__autocomplete task-list__autocomplete--executor"
            label="Пациент"
            hide-details
            clearable
            item-text="title"
            item-value="value"
            v-bind:items="filterPatientList"
            v-model="patientId">
            <template v-slot:no-data>
              <p style="padding: 10px; margin:0;">Результатов нет</p>
            </template>
          </v-autocomplete>
          <div>
            <span class="date-range-title">Дата создания</span>
            <date-range-with-presets
              v-bind:today="$options.today"
              v-bind:is-show-button-clear="true"
              v-model="createdAtDate"
            />
          </div>
          <div>
            <span class="date-range-title">Дедлайн</span>
            <date-range-with-presets
              v-bind:today="$options.today"
              v-bind:is-show-button-clear="true"
              v-model="deadLineDate"
            />
          </div>
        </div>
        <div class="task-list__groups">
          <label
            v-for="(status) in taskStatusGroup"
            v-bind:key="status.title"
            class="task-list__group"
          >
            <input
              hidden
              type="radio"
              v-bind:id="status.title"
              v-bind:value="status.title"
              v-model="selectedTaskStatus"
              v-on:change="fetchTaskList"
            />
            <span
              class="task-list__header__group__label"
              v-bind:class="status.title"
            >
              {{ status.title === 'all' ? 'Все' : mapStatus(status.title) }} {{ status.count }}
            </span>
          </label>
        </div>
        <side-slide-transition>
          <confirm-side-dialog
            v-if="isTaskCreationFormOpen"
            v-on:cancel="closeTaskCreationForm"
            v-bind:need-confirm="needConfirmCreationForm"
            label="Создать задачу"
          >
            <form-task-creation
              v-bind:patients="patientList"
              v-bind:performers="performerList"
              v-bind:violations="createViolations"
              v-on:create-task="onSendCreateRequest"
              v-on:task-is-changed="needConfirmCreationForm = $event"
            />
          </confirm-side-dialog>
        </side-slide-transition>
        <side-slide-transition>
          <task-display
            v-if="isTaskDisplayOpen"
            v-on:close="isTaskDisplayOpen = false"
            v-on:edit-task="showEditingForm"
            v-on:delete-task="onSendDeleteRequest"
            v-on:complete-task="onChangeStatus($event, 'completed')"
            v-bind:task="selectedTask"
          />
        </side-slide-transition>
        <side-slide-transition>
          <confirm-side-dialog
            v-if="isTaskEditingOpen"
            v-on:cancel="closeEditingForm"
            v-bind:need-confirm="needConfirmEditingForm"
            label="Редактировать задачу"
          >
            <form-task-editing
              v-bind:performers="performerList"
              v-bind:patients="patientList"
              v-bind:violations="editViolations"
              v-bind:initial-task="editingTask"
              v-on:edit-task="onSendEditRequest"
              v-on:task-is-changed="needConfirmEditingForm = $event"
            />
          </confirm-side-dialog>
        </side-slide-transition>
        <task-table
          v-bind:task-list="filteredTaskList"
          v-bind:is-await-request="isAwaitRequest"
          v-on:show-task="showTaskDisplay"
          v-on:change-status="onChangeStatus"
          v-on:sort-task-list="onSortTaskList"
        />
      </div>
    </main>
  </available-section>
</template>

<script>
import TaskTable from '@/components/TaskTable.vue';
import notifierMixin from '@/mixins/notifierMixin';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import FormTaskCreation from '@/components/FormTaskCreation.vue';
import FormTaskEditing from '@/components/FormTaskEditing.vue';
import TaskDisplay from '@/components/TaskDisplay.vue';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import {
  FILTER_TASK_AUTHOR_ID,
  FILTER_TASK_PERFORMER_ID,
  FILTER_TASK_PATIENT_ID,
} from '@/store/actions/tasks';
import { mapStatus, ViewTaskListRequest } from '@/storage/gateways/TaskGateway';
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';

/**
 * Миксин редактирования задачи
 */
const taskEditingMixin = {
  data() {
    return {
      /** Открыта ли форма редактирования? */
      isTaskEditingOpen: false,
      /** Нужно подтверждение для закрытия формы редактирвания */
      needConfirmEditingForm: false,
      /** Редактируемая задача */
      editingTask: {},
      /** Ошибки при редактировании */
      editViolations: {},
    };
  },
  methods: {
    /**
     * @description Отправить запрос на редактирование
     * @param {TaskRequest} request
     * @param {Number}      id
     */
    onSendEditRequest(request, id) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.isTaskEditingOpen = false;
        this.editViolations = {};
        this.fetchTaskList();
      };
      const onFail = (error) => {
        this.editViolations = error.allCauses();
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.editTask(request, id).then((result) => {
        result.fold(onFail, onSuccess);
      });
    },
    /**
     * @description Отправить запрос на удаление
     * @param {number} id Идентификатор задачи
     */
    onSendDeleteRequest(id) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.isTaskDisplayOpen = false;
        this.fetchTaskList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.deleteTask(id).then((result) => {
        result.fold(onFail, onSuccess);
      });
    },
    /**
     * @description Отправить запрос на изменение статуса задачи
     * @param {number} id Идентификатор задачи
     * @param {string} status Название статуса
     */
    onChangeStatus(id, status) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.isTaskDisplayOpen = false;
        this.fetchTaskList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.changeStatus(id, status).then((result) => {
        result.fold(onFail, onSuccess);
      });
    },
    /**
     * @description Показать форму редактирования
     */
    showEditingForm() {
      this.editingTask = this.selectedTask;
      this.isTaskEditingOpen = true;
      this.isTaskDisplayOpen = false;
    },
    /**
     * @description Закрыть форму редактирования
     */
    closeEditingForm() {
      this.editViolations = {};
      this.isTaskEditingOpen = false;
      this.needConfirmEditingForm = false;
    },
  },
};

/**
 * Миксин отображение задачи
 */
const taskDisplayMixin = {
  data() {
    return {
      /** Открыт ли показ задачи */
      isTaskDisplayOpen: false,
      /** Выбранная задача для показа */
      selectedTask: {},
    };
  },
  methods: {
    /**
     * @description Показать задачу в боковой модалке
     * @param {number} taskId Идентификатор задачи
     */
    showTaskDisplay(taskId) {
      const onSuccess = (task) => {
        this.isTaskDisplayOpen = true;
        this.selectedTask = task;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.getTaskById(taskId).then((response) => {
        response.fold(onFail, onSuccess);
      });
    },
  },
};

/**
 * Миксин формы создания задачи
 */
const formTaskCreationMixin = {
  data() {
    return {
      /** Нужно подтверждение закрытия формы создания */
      needConfirmCreationForm: false,
      /** Открыта ли форма создания задачи */
      isTaskCreationFormOpen: false,
      /** Ошибки при создании */
      createViolations: {},
    };
  },
  methods: {
    /**
     * @description Отправить запрос на создание задачи
     * @param {TaskRequest} request
     */
    onSendCreateRequest(request) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.closeTaskCreationForm();
        this.fetchTaskList();
      };
      const onFail = (error) => {
        this.createViolations = error.allCauses();
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.createTask(request).then((result) => {
        result.fold(onFail, onSuccess);
      });
    },
    /**
     * @description Показать форму создания задачи
     */
    showTaskCreationForm() {
      this.isTaskCreationFormOpen = true;
    },
    /**
     * @description Закрыть форму создания задачи
     */
    closeTaskCreationForm() {
      this.isTaskCreationFormOpen = false;
      this.createViolations = {};
    },
  },
};

const titleToWieghtMap = {
  all: 5,
  new: 4,
  work: 3,
  completed: 2,
  canceled: 1,
};

export default {
  components: {
    DateRangeWithPresets,
    AvailableSection,
    TaskDisplay,
    FormTaskCreation,
    FormTaskEditing,
    ConfirmSideDialog,
    SideSlideTransition,
    TaskTable,
  },
  created() {
    this.fetchTaskList();
    this.fetchOptionsList();
  },
  computed: {
    authorId: {
      get() {
        return this.$store.getters.getFilterTaskAuthorId;
      },
      set(newValue) {
        this.$store.commit(FILTER_TASK_AUTHOR_ID, newValue ?? 0);
      },
    },
    performerId: {
      get() {
        return this.$store.getters.getFilterTaskPerformerId;
      },
      set(newValue) {
        this.$store.commit(FILTER_TASK_PERFORMER_ID, newValue ?? 0);
      },
    },
    patientId: {
      get() {
        return this.$store.getters.getFilterTaskPatientId;
      },
      set(newValue) {
        this.$store.commit(FILTER_TASK_PATIENT_ID, newValue ?? 0);
      },
    },
  },
  watch: {
    authorId: { handler: 'fetchTaskList' },
    performerId: { handler: 'fetchTaskList' },
    patientId: { handler: 'fetchTaskList' },
    createdAtDate: { handler: 'fetchTaskList' },
    deadLineDate: { handler: 'fetchTaskList' },
    selectedTaskStatus: { handler: 'filterTaskListByStatus' },
  },
  data() {
    return {
      /** Список задач */
      taskList: [],
      /** Список исполнителей */
      performerList: [],
      /** Список пациентов */
      patientList: [],
      /** Список исполнителей для фильтра */
      filterPerformerList: [],
      /** Список пациентов для фильтра */
      filterPatientList: [],
      /** Тип сортировки */
      sortOrder: { type: '', order: '' },
      /** Выбранный статус задач */
      selectedTaskStatus: '',
      /** Группа статусов задач */
      taskStatusGroup: [],
      /** Отфильтрованный список задач */
      filteredTaskList: [],
      /** Выбранный промежуток даты постановки задачи */
      createdAtDate: [],
      /** Выбранный промежуток даты дедлайна */
      deadLineDate: [],
      /**
       * @description Ожидание завершения запроса.
       * @type {boolean}
       */
      isAwaitRequest: false,
    };
  },
  methods: {
    mapStatus,
    /**
     * @description Затянуть список задач
     */
    fetchTaskList() {
      const onSuccess = (tasks) => {
        this.taskList = tasks;
        this.updateTaskStatusGroup();
        this.filterTaskListByStatus();
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      this.isAwaitRequest = true;
      const request = new ViewTaskListRequest(
        this.sortOrder,
        this.authorId,
        this.performerId,
        this.patientId,
        this.createdAtDate,
        this.deadLineDate,
      );
      this.$taskGateway.getTaskList(request)
        .then(
          (result) => {
            result.fold(onFail, onSuccess);
            this.isAwaitRequest = false;
          },
        );
    },
    /**
     * @description Затянуть список исполнителей
     */
    fetchOptionsList() {
      const onSuccess = ({ performers, patients }) => {
        this.performerList = performers;
        this.patientList = patients;
        this.filterPerformerList = this.performerList;
        this.filterPatientList = this.patientList;
      };
      this.$taskGateway.getTaskOptions().then((result) => {
        result.fold(() => {}, onSuccess);
      });
    },
    /**
     * @description Сортировать список список задач
     */
    onSortTaskList(sort) {
      this.sortOrder = sort;
      this.fetchTaskList();
    },
    /**
     * @description Сортировать список список задач по статусу
     */
    filterTaskListByStatus() {
      if (this.selectedTaskStatus && this.selectedTaskStatus !== 'all') {
        this.filteredTaskList = this.taskList.filter(
          (task) => task.status === this.selectedTaskStatus,
        );
      } else {
        this.filteredTaskList = this.taskList;
      }
    },
    /**
     * @description Обновить сгрупированный список статусов задач
     */
    updateTaskStatusGroup() {
      this.taskStatusGroup = [];
      if (this.taskList.length === 0) {
        return;
      }
      this.taskList.forEach((task) => {
        if (this.taskStatusGroup.filter((status) => status.title === task.status).length) {
          this.taskStatusGroup.find((status) => status.title === task.status).count += 1;
        } else {
          this.taskStatusGroup.push({ title: task.status, count: 1 });
        }
      });
      this.taskStatusGroup.push({ title: 'all', count: this.taskList.length });
      this.taskStatusGroup.sort((a, b) => titleToWieghtMap[b.title] - titleToWieghtMap[a.title]);
    },
  },
  mixins: [notifierMixin, formTaskCreationMixin, taskDisplayMixin, taskEditingMixin],
  name: 'TaskList',
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.task-list {
  display: flex;
  height: 100%;
  width: 100%;
  &__body {
    padding: 38px 20px 48px 23px;
    width: 100%;
    overflow-y: scroll;
    overflow-x: hidden;
  }
  &__header {
    display: flex;
    flex-flow: row wrap;
    gap: 20px;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 43px 0;
  }
  &__filters {
    display: grid;
    gap: 20px;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 43px 0;
    grid-template-columns: 30% 30% 30%;
    ::v-deep .date-range-with-presets {
      flex-wrap: wrap;
      &__search {
        min-width: 250px;
      }
    }
    .date-range-title {
      display: block;
      margin: 0 0 2px 0;
      color: var(--unfocus-color);
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
    }
  }
  &__groups {
    display: flex;
    flex-flow: row wrap;
    gap: 6px;
    margin: 10px 0;
  }
  &__group {
    display: flex;
    align-items: center;
    &>input {
      position: absolute;
      z-index: -1;
      opacity: 0;
      &:checked+span {
        border-color: var(--action-color);
        border-width: 2px;
        padding: 7px 14px;
      }
    }
    &>span {
      display: flex;
      align-items: center;
      color: var(--reading-color);
      font-size: 14px;
      padding: 8px 15px;
      border: 1px solid #c2cfe0;
      border-radius: 4px;
      cursor: pointer;
      &:hover {
        border-color: var(--action-color);
        border-width: 2px;
        padding: 7px 14px;
      }
      &:before {
        content: '';
      }
      &.all:before {
        content: none;
      }
      &.canceled:before {
        width: 20px;
        height: 20px;
        margin-right: 5px;
        background-image: url("@/assets/img/exit-red.svg");
        background-repeat: no-repeat;
        background-size: contain;
      }
      &.completed:before {
        height: 20px;
        width: 20px;
        margin-right: 10px;
        background-image: url("@/assets/img/checked/all-checked-green.svg");
        background-repeat: no-repeat;
        background-size: contain;
      }
      &.new:before {
        height: 6px;
        width: 6px;
        margin-right: 10px;
        background-color: #abb1a3;
        border-radius: 50%;
      }
      &.work:before {
        height: 15px;
        width: 15px;
        margin-right: 10px;
        background-image: url("@/assets/img/checked/checked-green-light.svg");
        background-repeat: no-repeat;
        background-size: contain;
      }
    }
  }
  &__autocomplete {
    &.v-input .v-label {
      top: 3px
    }
  }
  &__button {
    &-add {
      @include buttons.btn-blue(187px, 48px, 16px);
      @include buttons.btn-blue-add(19px);
    }
  }
}

@media screen and (max-width: 600px) {
  .task-list {
    &__filters {
      grid-template-columns: 1fr;
      max-width: 300px;
    }
    &__autocomplete {
      &--executor {
        grid-row: 3/4;
      }
    }
  }
}
</style>
